import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const CreateOrderModal = ({ 
  isOpen, 
  onClose, 
  vehicleData, 
  onCreateOrder, 
  isLoading = false 
}) => {
  const [notes, setNotes] = useState('');
  
  // Fahrtennachweis specific fields
  const [startziel, setStartziel] = useState('');
  const [ueber, setUeber] = useState('');
  const [endziel, setEndziel] = useState('');
  const [fahrtzweck, setFahrtzweck] = useState('');
  const [fahrerName, setFahrerName] = useState('');
  const [fahrerAdresse, setFahrerAdresse] = useState('');
  const [fahrtEnde, setFahrtEnde] = useState('');



  // Reset form when modal opens/closes and load data
  useEffect(() => {
    if (isOpen) {
      setNotes('');
      // Reset Fahrtennachweis fields
      setStartziel('');
      setUeber('');
      setEndziel('');
      setFahrtzweck('');
      setFahrerName('');
      setFahrerAdresse('');
      setFahrtEnde('');
    }
  }, [isOpen]);



  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!fahrtzweck) {
      alert('Bitte wählen Sie einen Fahrtzweck aus.');
      return;
    }

    const orderData = {
      vehicleData,
      notes,
      // Fahrtennachweis data
      startziel,
      ueber,
      endziel,
      fahrtzweck,
      fahrerName,
      fahrerAdresse,
      fahrtEnde,
      status: 'NEW',
      createdAt: new Date().toISOString()
    };

    onCreateOrder(orderData);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-foreground">Neuen Auftrag erstellen</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Serviceauftrag für {vehicleData?.make} {vehicleData?.model} ({vehicleData?.vin})
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              disabled={isLoading}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Vehicle Info */}
        <div className="p-6 border-b border-border bg-muted/30">
          <h3 className="text-sm font-semibold text-foreground mb-3">Fahrzeugdaten</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Fahrzeug:</span>
              <p className="font-medium">{vehicleData?.make} {vehicleData?.model}</p>
            </div>
            <div>
              <span className="text-muted-foreground">VIN:</span>
              <p className="font-mono text-xs">{vehicleData?.vin}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Fahrtennachweis Section */}
          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Fahrtennachweis Daten</h3>
            
            {/* Auto-populated data (read-only) */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-muted/30 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Datum (automatisch)
                </label>
                <div className="px-3 py-2 border border-border rounded-md bg-muted text-foreground">
                  {new Date().toLocaleDateString('de-DE')}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Uhrzeit Beginn (automatisch)
                </label>
                <div className="px-3 py-2 border border-border rounded-md bg-muted text-foreground">
                  {new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Uhrzeit Ende
                </label>
                <Input
                  type="time"
                  value={fahrtEnde}
                  onChange={(e) => setFahrtEnde(e.target.value)}
                  placeholder="HH:MM"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Benutztes Fahrzeug (automatisch)
                </label>
                <div className="px-3 py-2 border border-border rounded-md bg-muted text-foreground">
                  {vehicleData?.make} {vehicleData?.model}
                </div>
              </div>
            </div>

            {/* Fahrtstrecke */}
            <div className="mb-6">
              <h4 className="text-md font-medium text-foreground mb-3">Fahrtstrecke</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Startziel
                  </label>
                  <Input
                    type="text"
                    value={startziel}
                    onChange={(e) => setStartziel(e.target.value)}
                    placeholder="Startort eingeben"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    über
                  </label>
                  <Input
                    type="text"
                    value={ueber}
                    onChange={(e) => setUeber(e.target.value)}
                    placeholder="Zwischenstopp (optional)"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Endziel
                  </label>
                  <Input
                    type="text"
                    value={endziel}
                    onChange={(e) => setEndziel(e.target.value)}
                    placeholder="Zielort eingeben"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {/* Fahrtzweck */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Fahrtzweck *
              </label>
              <Select
                value={fahrtzweck}
                onChange={setFahrtzweck}
                placeholder="Fahrtzweck auswählen"
                disabled={isLoading}
                options={[
                  { value: 'innen-aussenreinigung', label: 'Innen-aussenreinigung' },
                  { value: 'zurueck-gefahren-fertig-sauber', label: 'Zurück gefahren fertig mit Sauber machen' },
                  { value: 'abholung-reinigung', label: 'Abholung für Reinigung' },
                  { value: 'zurueck-gefahren-fertig-pkw', label: 'Zurück gefahren fertiger PKW' }
                ]}
              />
            </div>

            {/* Fahrzeugführer */}
            <div className="mb-6">
              <h4 className="text-md font-medium text-foreground mb-3">Fahrzeugführer</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Name, Vorname
                  </label>
                  <Input
                    type="text"
                    value={fahrerName}
                    onChange={(e) => setFahrerName(e.target.value)}
                    placeholder="Nachname, Vorname"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    PLZ, Ort, Straße
                  </label>
                  <Input
                    type="text"
                    value={fahrerAdresse}
                    onChange={(e) => setFahrerAdresse(e.target.value)}
                    placeholder="PLZ, Ort, Straße"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notizen
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Zusätzliche Informationen zum Auftrag..."
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              rows={3}
              disabled={isLoading}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              disabled={!fahrtzweck}
              iconName="Plus"
              iconPosition="left"
            >
              Auftrag erstellen
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrderModal;
